import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SavedView } from '../../interfaces/views';
import { BackendFilter } from '../../interfaces/sessions';
import { RootState } from '../../store';
import { getServicesManager } from '../../services';
import { updateSavedViews } from '../../lib/redux/views/slice';
import { useUserData } from '../../lib/redux/users/user-data-hook';

interface SavedViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingView?: SavedView | null;
  currentFilter?: BackendFilter;
}

export const SavedViewModal: React.FC<SavedViewModalProps> = ({
  isOpen,
  onClose,
  editingView = null,
  currentFilter
}) => {
  const [name, setName] = useState('');
  const [isTeam, setIsTeam] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user.value);
  const savedViews = useSelector((state: RootState) => state.savedViews.value) || [];
  const { isAdmin } = useUserData();

  // Initialize form when editing view changes
  useEffect(() => {
    if (editingView) {
      setName(editingView.name);
      setIsTeam(editingView.is_team);
    } else {
      setName('');
      setIsTeam(false);
    }
    setError(null);
  }, [editingView]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!currentFilter && !editingView) {
      setError('No filter data available');
      return;
    }

    // Check team view permissions
    if (isTeam && !isAdmin) {
      setError('Only team administrators can create team views');
      return;
    }

    if (editingView?.is_team && !isAdmin) {
      setError('Only team administrators can edit team views');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const servicesManager = getServicesManager();
      
      if (editingView) {
        // Update existing view
        const updatedView: SavedView = {
          ...editingView,
          name: name.trim(),
          is_team: isTeam,
          filter: currentFilter || editingView.filter
        };
        
        const result = await servicesManager.updateSavedView(updatedView);
        if (result) {
          // Update the view in Redux state directly
          const updatedViews = savedViews.map(view => 
            view.view_id === result.view_id ? result : view
          );
          dispatch(updateSavedViews(updatedViews));
          onClose();
        } else {
          setError('Failed to update saved view');
        }
      } else {
        // Create new view
        const result = await servicesManager.addSavedView(
          name.trim(),
          isTeam,
          currentFilter!
        );
        
        if (result) {
          // Add new view to Redux state directly
          dispatch(updateSavedViews([...savedViews, result]));
          onClose();
        } else {
          setError('Failed to create saved view');
        }
      }
    } catch (err) {
      setError('An error occurred while saving the view');
      console.error('Error saving view:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!editingView) return;
    
    // Check team view permissions for deletion
    if (editingView.is_team && !isAdmin) {
      setError('Only team administrators can delete team views');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this saved view?')) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const servicesManager = getServicesManager();
      const result = await servicesManager.deleteSavedView(editingView.view_id);
      
      if (result) {
        // Remove view from Redux state directly
        const updatedViews = savedViews.filter(view => view.view_id !== editingView.view_id);
        dispatch(updateSavedViews(updatedViews));
        onClose();
      } else {
        setError('Failed to delete saved view');
      }
    } catch (err) {
      setError('An error occurred while deleting the view');
      console.error('Error deleting view:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-semibold mb-4">
          {editingView ? 'Edit Saved View' : 'Create Saved View'}
        </h2>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="viewName" className="block text-sm font-medium text-gray-700 mb-2">
              View Name
            </label>
            <input
              type="text"
              id="viewName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter view name..."
              disabled={isLoading}
            />
          </div>

          {/* Only show team sharing option if user is admin or editing their own non-team view */}
          {(isAdmin || (!editingView?.is_team && editingView?.user_id === user?.user_id) || !editingView) && (
            <div className="mb-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isTeam}
                  onChange={(e) => setIsTeam(e.target.checked)}
                  className="mr-2"
                  disabled={isLoading || (editingView?.is_team && !isAdmin)}
                />
                <span className="text-sm text-gray-700">
                  Share with team
                  {!isAdmin && (
                    <span className="text-xs text-gray-500 block">
                      (Team administrators only)
                    </span>
                  )}
                </span>
              </label>
            </div>
          )}

          <div className="flex justify-between">
            <div>
              {editingView && (editingView.user_id === user?.user_id || (editingView.is_team && isAdmin)) && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Delete
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || !name.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? 'Saving...' : (editingView ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}; 